import { nextTick } from "vue"

interface Job {
  mount: () => void
  cancelled: boolean
}
const high: Job[] = []
// Low-priority work waits until nothing else is queued: lookup rows nothing paints come after the rows a scroll would reach.
const low: Job[] = []
let scheduled = false

function whenIdle(callback: (deadline?: IdleDeadline) => void) {
  // A tab the browser does not paint gets no idle periods; the timeout keeps the queue moving there.
  return typeof requestIdleCallback === "function"
    ? requestIdleCallback(callback, { timeout: 1000 })
    : setTimeout(callback, 200)
}

async function drain(deadline?: IdleDeadline) {
  let job: Job | undefined
  while ((job = high.shift() ?? low.shift())) {
    if (job.cancelled) continue
    try {
      job.mount()
    } catch (error) {
      console.error("[idle-mount] A mount failed; the queue continues", error)
    }
    await nextTick()
    // Keep mounting while this idle period has room; a timed-out callback has none and mounts one.
    if (!deadline || deadline.timeRemaining() < 4) break
  }
  scheduled = false
  if (high.length || low.length) schedule()
}

function schedule() {
  if (scheduled) return
  scheduled = true
  whenIdle(drain)
}

/**
 * Runs `mount` in a browser idle period, callers in order: each waits for its render to flush
 * before the next starts, and an idle period mounts as many as it has time for, so a column
 * mounts its content group by group between frames instead of while the user scrolls to it.
 * Returns a function that cancels a pending mount.
 */
export function mountWhenIdle(mount: () => void, priority: "high" | "low" = "high"): () => void {
  const job: Job = { mount, cancelled: false }
  ;(priority === "low" ? low : high).push(job)
  schedule()
  return () => {
    job.cancelled = true
  }
}
